import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { config } from '../../../config';
import { isInteractiveUserProfile } from '../../../lib/isInteractiveMedplumProfile';

function isClientApplicationProfile(profile: unknown): boolean {
  return (profile as { resourceType?: string } | undefined)?.resourceType === 'ClientApplication';
}

/**
 * Ensures intake (when there is no interactive Practitioner/Patient session) uses the
 * dedicated OAuth client_credentials ClientApplication.
 *
 * We intentionally do **not** reuse Practitioner/Patient sessions from the main app:
 * those AccessPolicies cannot search all open referral-request Tasks.
 */
export function useIntakeClientAuth(opts?: {
  /** Staff `/settings/users/add-client` — keep Practitioner session; no public OAuth. */
  staffAddClient?: boolean;
}): { ready: boolean; authError: string | null } {
  const medplum = useMedplum();
  const staffAddClient = Boolean(opts?.staffAddClient);
  const [ready, setReady] = useState(() => {
    const profile = medplum.getProfile();
    if (profile?.resourceType === 'Patient') return true;
    return isClientApplicationProfile(profile);
  });
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const profile = medplum.getProfile();

    if (staffAddClient) {
      if (profile?.resourceType === 'Practitioner') {
        setReady(true);
        setAuthError(null);
        return;
      }
      setReady(false);
      setAuthError('Add client requires a signed-in staff (Practitioner) session.');
      return;
    }

    if (profile?.resourceType === 'Patient') {
      setReady(true);
      setAuthError(null);
      return;
    }

    if (isClientApplicationProfile(profile)) {
      setReady(true);
      setAuthError(null);
      return;
    }

    if (isInteractiveUserProfile(profile)) {
      // Reuse signed-in Patient/RelatedPerson sessions as-is for authenticated portal intake.
      // We must NOT call startClientLogin while a human is signed in — that replaces the
      // global session and signs them out.
      setReady(true);
      setAuthError(null);
      return;
    }

    const { clientId, clientSecret } = config.medplum;
    if (!clientId || !clientSecret) {
      setReady(false);
      setAuthError(
        'Public intake OAuth is not configured. Set VITE_MEDPLUM_CLIENT_ID and VITE_MEDPLUM_CLIENT_SECRET.',
      );
      return;
    }

    let cancelled = false;
    setReady(false);
    setAuthError(null);

    void medplum
      .startClientLogin(clientId, clientSecret)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setAuthError(
            e instanceof Error ? e.message : 'Failed to authenticate public intake client',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, staffAddClient]);

  return { ready, authError };
}

/** @deprecated Prefer `useIntakeClientAuth` via IntakeDataProvider. */
export function useEnsureIntakeClientCredentials(): void {
  useIntakeClientAuth();
}
